import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Lightbulb, Camera, Eye, Sun, Sparkles, ArrowRight, Zap, Shield, Star, Heart, BarChart3, Battery } from 'lucide-react';
import { mockData } from './mock';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

const iconComponents = {
  Lightbulb,
  Camera,
  Eye,
  Sun,
  Sparkles,
  Zap,
  BarChart3,
  Battery
};

export const ServicesSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [servicesRef, servicesVisible] = useScrollAnimation({ delay: 200 });
  const [additionalRef, additionalVisible] = useScrollAnimation({ delay: 400 });

  const handleLearnMore = (serviceId) => {
    window.location.href = `/service/${serviceId}`;
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-80 h-80 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-200/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-green-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-12 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6 border border-blue-200/50">
            <Zap size={14} className="sm:w-4 sm:h-4 text-blue-600" />
            <Badge className="bg-transparent text-blue-700 px-0 py-0 text-xs sm:text-sm font-semibold">
              Our Services
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            Comprehensive
            <span 
              className="block bg-clip-text text-transparent"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))' 
              }}
            >
              Traffic Technology Solutions
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
            From smart street lighting to advanced traffic enforcement, we provide 
            end-to-end solutions that transform urban infrastructure and create 
            intelligent, sustainable cities for tomorrow.
          </p>
        </div>

        {/* Services Grid */}
        <div ref={servicesRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16 lg:mb-20">
          {mockData.services.map((service, index) => {
            const IconComponent = iconComponents[service.icon];
            const gradients = [
              'from-blue-500 to-indigo-600',
              'from-green-500 to-teal-600', 
              'from-purple-500 to-pink-600',
              'from-orange-500 to-red-600',
              'from-cyan-500 to-blue-600',
              'from-yellow-500 to-orange-600',
              'from-indigo-500 to-purple-600',
              'from-emerald-500 to-green-600'
            ];
            const cardGradients = [
              'from-blue-50/80 to-indigo-50/80',
              'from-green-50/80 to-teal-50/80',
              'from-purple-50/80 to-pink-50/80',
              'from-orange-50/80 to-red-50/80',
              'from-cyan-50/80 to-blue-50/80',
              'from-yellow-50/80 to-orange-50/80',
              'from-indigo-50/80 to-purple-50/80',
              'from-emerald-50/80 to-green-50/80'
            ];
            
            return (
              <Card key={service.id} className="group relative overflow-hidden h-full border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 bg-white/90 backdrop-blur-sm">
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${cardGradients[index]} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                
                {/* Floating Elements */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <Star size={16} className="text-yellow-400 animate-pulse" />
                </div>
                
                <CardHeader className="relative z-10 pb-2 sm:pb-4 p-4 sm:p-6">
                  <div className="relative mb-4 sm:mb-6">
                    <div className="relative overflow-hidden rounded-xl sm:rounded-2xl">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className={`absolute top-3 left-3 sm:top-4 sm:left-4 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${gradients[index]} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                      <IconComponent size={16} className="sm:w-5 sm:h-5 text-white" />
                    </div>
                  </div>
                  <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors leading-tight">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="relative z-10 pt-0 p-4 sm:p-6">
                  <CardDescription className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                    {service.description}
                  </CardDescription>
                  
                  {/* Features */}
                  <div className="grid grid-cols-1 gap-2 sm:gap-3 mb-4 sm:mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-gray-700 group/feature">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full mr-2 sm:mr-3 group-hover/feature:scale-125 transition-transform duration-300 flex-shrink-0"></div>
                        <span className="font-semibold text-xs sm:text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    size="lg"
                    variant="ghost" 
                    onClick={() => handleLearnMore(service.id)}
                    className="w-full justify-between group-hover:bg-white/90 group-hover:text-blue-600 transition-all duration-300 border border-gray-200 group-hover:border-blue-200 rounded-lg sm:rounded-xl py-3 sm:py-4 text-sm sm:text-base"
                  >
                    <span className="font-semibold">Learn More</span>
                    <ArrowRight size={16} className="sm:w-5 sm:h-5 group-hover:translate-x-2 transition-transform duration-300" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Services Grid */}
        <div ref={additionalRef} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="group relative overflow-hidden p-8 bg-gradient-to-br from-blue-50/80 to-indigo-50/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 backdrop-blur-sm">
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <Shield size={20} className="text-blue-500 animate-pulse" />
            </div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-4 py-2 rounded-full mb-6">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-blue-700 text-sm font-semibold">Advanced Systems</span>
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 group-hover:text-blue-600 transition-colors">
              Intelligent Transportation Systems (ITS)
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed text-lg">
              Comprehensive traffic management solutions including traffic signal systems, 
              flow optimization, and real-time monitoring for improved urban mobility and 
              enhanced safety across all transportation networks.
            </p>
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
              View ITS Solutions
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Card>

          <Card className="group relative overflow-hidden p-8 bg-gradient-to-br from-green-50/80 to-emerald-50/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 backdrop-blur-sm">
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <Heart size={20} className="text-green-500 animate-pulse" />
            </div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 px-4 py-2 rounded-full mb-6">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-green-700 text-sm font-semibold">Smart Integration</span>
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 group-hover:text-green-600 transition-colors">
              Smart City Integration
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed text-lg">
              Seamless integration with existing city infrastructure to create connected, 
              intelligent urban environments with IoT sensors, data analytics, and 
              predictive maintenance systems.
            </p>
            <Button size="lg" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
              Explore Integration
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Card>
        </div>
      </div>
    </section>
  );
};